/**
 * Tomo Cloud API contract — shared by `apps/server` (implementation) and `apps/web` (client).
 *
 * Tier model
 * ----------
 * - Free: every feature, fully local (IndexedDB in the browser). No account needed.
 * - Pro:  a workspace on the `pro` plan unlocks cloud projects: real-time collaborative
 *         editing (Yjs over WebSocket), sync between devices, and team members.
 *
 * Transport
 * ---------
 * - REST under `/api`, JSON bodies, cookie session (`tomo_session`, HttpOnly, SameSite=Lax).
 *   Mutating requests must send `Content-Type: application/json` (CSRF guard) unless noted.
 * - Errors: non-2xx with body `ApiError`.
 * - Real-time sync: a WebSocket per project at `/sync/<projectId>?token=…` speaking the standard
 *   Yjs sync + awareness protocol (y-websocket framing, served by y-partyserver). The token is a
 *   short-lived sync token from `POST /api/projects/:id/sync-token`; the client fetches a fresh
 *   one on every (re)connect. When the server refuses or later revokes a connection it closes the
 *   socket with `syncCloseCode(reason)` and the `SyncCloseReason` as the close reason.
 */

export type Plan = 'free' | 'pro';
export type Role = 'owner' | 'admin' | 'editor' | 'viewer';
export type BillingMode = 'stripe' | 'dev' | 'disabled';

export const ROLE_RANK: Record<Role, number> = { viewer: 0, editor: 1, admin: 2, owner: 3 };

/** True when `role` is at least `min` (owner > admin > editor > viewer). */
export function roleAtLeast(role: Role, min: Role): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[min];
}

export const SYNC_PATH = '/sync';
export const SESSION_COOKIE = 'tomo_session';
/** WebSocket path of one project's sync room. */
export const syncPath = (projectId: string) => `${SYNC_PATH}/${encodeURIComponent(projectId)}`;

/** Why the server refused or closed a sync connection (sent as the WebSocket close reason). */
export type SyncCloseReason =
  | 'invalid-token'
  | 'token-expired'
  | 'payment-required'
  | 'forbidden'
  | 'not-found'
  | 'project-deleted'
  | 'workspace-deleted'
  | 'workspace-downgraded'
  | 'member-removed'
  | 'role-changed';

/** Application close codes (4000–4999), grouped like the matching HTTP statuses. */
export function syncCloseCode(reason: SyncCloseReason): number {
  switch (reason) {
    case 'invalid-token':
    case 'token-expired':
      return 4401;
    case 'payment-required':
    case 'workspace-downgraded':
      return 4402;
    case 'forbidden':
    case 'member-removed':
    case 'role-changed':
      return 4403;
    case 'not-found':
    case 'project-deleted':
    case 'workspace-deleted':
      return 4404;
  }
}

/** Reasons after which reconnecting (with a fresh token) can succeed; the rest are final. */
export function isRecoverableSyncClose(reason: string): boolean {
  return reason === 'invalid-token' || reason === 'token-expired' || reason === 'role-changed';
}

export interface ApiError {
  error: {
    code:
      | 'bad_request'
      | 'unauthorized'
      | 'forbidden'
      | 'not_found'
      | 'conflict'
      | 'payment_required'
      | 'too_large'
      | 'rate_limited'
      | 'internal';
    message: string;
  };
}

export interface ApiUser {
  id: string;
  email: string;
  name: string;
  /** Hex color used for avatars and live cursors, e.g. `#E8A33D`. */
  color: string;
  createdAt: number;
  /** Proven by opening an emailed link. Invites locked to an address need it (when the server sends email). */
  emailVerified: boolean;
}

export interface ApiWorkspace {
  id: string;
  name: string;
  plan: Plan;
  /** Paid seats (Stripe quantity). `null` = unlimited (dev billing). */
  seats: number | null;
  /** The requesting user's role in this workspace. */
  role: Role;
  memberCount: number;
  createdAt: number;
  billing: {
    mode: BillingMode;
    /** Stripe subscription status when mode = 'stripe' (e.g. `active`, `past_due`). */
    status: string | null;
    currentPeriodEnd: number | null;
  };
}

export interface ApiMember {
  userId: string;
  email: string;
  name: string;
  color: string;
  role: Role;
  joinedAt: number;
}

export interface ApiInvite {
  id: string;
  workspaceId: string;
  /** Optional: restrict the invite to one email address. */
  email: string | null;
  role: Exclude<Role, 'owner'>;
  createdAt: number;
  expiresAt: number;
  /** Only present in the response that created the invite (never listed again). */
  token?: string;
  /** Convenience absolute URL: `${APP_ORIGIN}/invite/${token}` (creation response only). */
  url?: string;
  /** Whether the link was emailed to `email` (creation response only). False without an address, or when sending failed. */
  emailed?: boolean;
}

export interface ApiInvitePreview {
  workspaceName: string;
  role: Exclude<Role, 'owner'>;
  invitedBy: string;
  expiresAt: number;
  /** When the invite is locked to an email address. */
  email: string | null;
}

export interface ApiProject {
  id: string;
  workspaceId: string;
  name: string;
  icon: string | null;
  color: string | null;
  createdAt: number;
  createdBy: string;
  updatedAt: number;
}

export interface ApiSyncToken {
  token: string;
  expiresAt: number;
  /** viewers get a read-only connection */
  role: Role;
  /** WebSocket path to connect to (relative to the API origin), `/sync/<projectId>`. */
  path: string;
}

export interface ApiBillingConfig {
  mode: BillingMode;
  /** Human readable price, e.g. "$8 per member / month". */
  priceLabel: string;
}

/* ------------------------------------------------------------------------------------------
 * Endpoint reference (request → response)
 * ------------------------------------------------------------------------------------------
 * Auth
 *   POST   /api/auth/signup            SignupBody            → { user: ApiUser }       (sets cookie)
 *   POST   /api/auth/login             LoginBody             → { user: ApiUser }       (sets cookie)
 *   POST   /api/auth/logout                                  → { ok: true }            (clears cookie)
 *   GET    /api/session                                      → SessionResponse         (always 200; user null when signed out)
 *   GET    /api/me                                           → MeResponse              (401 when signed out)
 *   PATCH  /api/me                     UpdateMeBody          → { user: ApiUser }
 *
 * Email (sent through Cloudflare Email Service; 503 when the server has no email set up)
 *   POST   /api/auth/forgot-password   { email }             → { ok: true }            (same answer whether or not the account exists)
 *   POST   /api/auth/reset-password    ResetPasswordBody     → { user: ApiUser }       (signs out other sessions; sets cookie)
 *   POST   /api/auth/verify-email      { token }             → { ok: true, email }     (public; the link keeps working until it expires)
 *   POST   /api/auth/verify-email/resend                     → { ok: true }            (signed in; 409 when already verified)
 *   Links in the emails: `${APP_ORIGIN}/reset-password/<token>` (1 hour, once), `${APP_ORIGIN}/verify-email/<token>` (7 days).
 *
 * Workspaces
 *   GET    /api/workspaces                                   → ApiWorkspace[]
 *   POST   /api/workspaces             { name }              → ApiWorkspace            (caller becomes owner)
 *   PATCH  /api/workspaces/:id         { name }              → ApiWorkspace            (admin+)
 *   DELETE /api/workspaces/:id                               → { ok: true }            (owner)
 *   GET    /api/workspaces/:id/members                       → ApiMember[]             (viewer+)
 *   PATCH  /api/workspaces/:id/members/:userId { role }      → ApiMember               (admin+, cannot demote last owner)
 *   DELETE /api/workspaces/:id/members/:userId               → { ok: true }            (admin+, or self = leave)
 *   GET    /api/workspaces/:id/invites                       → ApiInvite[]             (admin+)
 *   POST   /api/workspaces/:id/invites { email?, role }      → ApiInvite (with token)  (admin+; emails the link when an address is given)
 *   DELETE /api/workspaces/:id/invites/:inviteId             → { ok: true }            (admin+)
 *   GET    /api/invites/:token                               → ApiInvitePreview        (public)
 *   POST   /api/invites/:token/accept                        → ApiWorkspace            (signed in)
 *
 * Cloud projects (require the workspace to be on the `pro` plan)
 *   GET    /api/workspaces/:id/projects                      → ApiProject[]            (viewer+, any plan)
 *   POST   /api/workspaces/:id/projects CreateProjectBody    → ApiProject              (editor+, pro; client supplies id)
 *   PATCH  /api/projects/:id           { name?, icon?, color? } → ApiProject           (editor+)
 *   DELETE /api/projects/:id                                 → { ok: true }            (admin+)
 *   POST   /api/projects/:id/sync-token                      → ApiSyncToken            (viewer+, pro)
 *   WS     /sync/:projectId?token=<sync token>              → Yjs sync + awareness     (viewers are read-only)
 *
 * Blobs (content-addressed asset files; hash = lowercase hex SHA-256 of the bytes)
 *   POST   /api/projects/:id/blobs/check { hashes: string[] } → { missing: string[] }  (viewer+)
 *   PUT    /api/projects/:id/blobs/:hash  <raw bytes>        → { hash, size }          (editor+, pro; hash verified)
 *   GET    /api/projects/:id/blobs/:hash                     → raw bytes               (viewer+; immutable cache headers)
 *
 * Billing
 *   GET    /api/billing/config                               → ApiBillingConfig        (public)
 *   POST   /api/workspaces/:id/billing/checkout              → { url }                 (owner/admin; stripe mode)
 *   POST   /api/workspaces/:id/billing/portal                → { url }                 (owner/admin; stripe mode)
 *   POST   /api/workspaces/:id/billing/dev-plan { plan }     → ApiWorkspace            (owner/admin; dev mode only)
 *   POST   /api/billing/webhook          <stripe payload>    → { received: true }      (Stripe signature verified)
 *
 * Health
 *   GET    /api/health                                       → { ok: true, version }
 * ------------------------------------------------------------------------------------------ */

export interface SignupBody {
  email: string;
  password: string;
  name: string;
}

export interface LoginBody {
  email: string;
  password: string;
}

export interface UpdateMeBody {
  name?: string;
  color?: string;
}

export interface ResetPasswordBody {
  /** From the emailed link. */
  token: string;
  password: string;
}

export interface MeResponse {
  user: ApiUser;
  workspaces: ApiWorkspace[];
}

/** App start probe: like MeResponse, but a signed-out visitor gets `user: null` instead of a 401. */
export interface SessionResponse {
  user: ApiUser | null;
  workspaces: ApiWorkspace[];
  /** Whether this server sends email (invites, password resets, verification links). */
  sendsEmail: boolean;
}

export interface CreateProjectBody {
  /** Client-generated id so a local project keeps its identity when moved to the cloud. */
  id: string;
  name: string;
  icon?: string | null;
  color?: string | null;
}

export const PASSWORD_MIN_LENGTH = 8;
export const MAX_BLOB_BYTES_DEFAULT = 100 * 1024 * 1024;
